import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Mail, Printer, RefreshCw, Save, ScanLine, ServerCog } from "lucide-react";
import { Button, ErrorBanner, Field, Input, LoadingState, PageHeader, Panel, StatCard, StatusBadge } from "../components/Ui";
import { requireSupabase } from "../lib/supabase";
import { formatDateTime } from "../lib/warehouseData";
import { useDeveloperSettings } from "../lib/developerSettings";

type QueueRow = {
  id: string;
  event_type: "goods_receipt_stored" | "pickup_completed";
  recipient: string | null;
  status: "pending" | "processing" | "completed" | "failed";
  attempts: number;
  last_error: string | null;
  created_at: string;
  sent_at: string | null;
};

type ScannerAgentRow = {
  warehouse_id: string;
  device_name: string;
  source_folder: string;
  agent_version: string;
  status: "online" | "folder_unreachable" | "error";
  last_heartbeat_at: string;
  last_import_at: string | null;
  last_import_file_name: string | null;
  last_error: string | null;
};

type WarehouseRow = { id: string; name: string };

function scannerStatus(agent: ScannerAgentRow) {
  const heartbeat = new Date(agent.last_heartbeat_at).getTime();
  if (!Number.isFinite(heartbeat) || Date.now() - heartbeat > 120_000) return { label: "Offline", tone: "danger" as const };
  if (agent.status === "folder_unreachable") return { label: "Ordner fehlt", tone: "warning" as const };
  if (agent.status === "error") return { label: "Fehler", tone: "danger" as const };
  return { label: "Online", tone: "success" as const };
}

export function SystemPage() {
  const { exampleTexts } = useDeveloperSettings();
  const [email, setEmail] = useState("");
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [printJobs, setPrintJobs] = useState(0);
  const [scannerAgents, setScannerAgents] = useState<ScannerAgentRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const client = requireSupabase();
    const [settingResult, queueResult, printResult, scannerResult, warehouseResult] = await Promise.all([
      client.from("app_settings").select("value").eq("key", "dispo_email").maybeSingle(),
      client.from("dispo_email_queue").select("id,event_type,recipient,status,attempts,last_error,created_at,sent_at").order("created_at", { ascending: false }).limit(30),
      client.from("label_print_jobs").select("id", { count: "exact", head: true }).in("status", ["queued", "failed"]),
      client.from("scanner_agent_status").select("warehouse_id,device_name,source_folder,agent_version,status,last_heartbeat_at,last_import_at,last_import_file_name,last_error").order("last_heartbeat_at", { ascending: false }),
      client.from("warehouses").select("id,name")
    ]);
    const firstError = settingResult.error || queueResult.error || printResult.error || scannerResult.error || warehouseResult.error;
    if (firstError) setError(firstError.message);
    else {
      setEmail(typeof settingResult.data?.value === "string" ? settingResult.data.value : "");
      setQueue((queueResult.data ?? []) as QueueRow[]);
      setPrintJobs(printResult.count ?? 0);
      setScannerAgents((scannerResult.data ?? []) as ScannerAgentRow[]);
      setWarehouses((warehouseResult.data ?? []) as WarehouseRow[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function saveEmail(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSuccess("");
    const { error: rpcError } = await requireSupabase().rpc("configure_dispo_email", { target_email: email.trim() || null });
    if (rpcError) setError(rpcError.message);
    else {
      setSuccess(email.trim() ? "Dispo-Adresse gespeichert und für offene Meldungen übernommen." : "Dispo-Adresse wurde entfernt.");
      await load();
    }
    setBusy(false);
  }

  const pendingEmails = queue.filter((row) => row.status === "pending" || row.status === "failed").length;
  const completedEmails = queue.filter((row) => row.status === "completed").length;
  const onlineScanners = scannerAgents.filter((agent) => scannerStatus(agent).label === "Online").length;

  return (
    <>
      <PageHeader title="Systemstatus" description="Konfiguration und Warteschlangen der Testumgebung." action={<Button variant="secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button>} />
      {error && <ErrorBanner message={error} />}
      {success && <div className="success-banner"><ServerCog size={17} />{success}</div>}
      <section className="stat-grid">
        <StatCard label="E-Mail offen" value={pendingEmails} hint="wartet auf Versanddienst" icon={Mail} tone={pendingEmails ? "orange" : "green"} />
        <StatCard label="E-Mail versendet" value={completedEmails} hint="in letzter Warteschlangenansicht" icon={Mail} tone="green" />
        <StatCard label="Etiketten offen" value={printJobs} hint="Druckerwarteschlange" icon={Printer} tone={printJobs ? "orange" : "green"} />
        <StatCard label="Scanner online" value={onlineScanners} hint={`${scannerAgents.length} Agenten eingerichtet`} icon={ScanLine} tone={onlineScanners === scannerAgents.length && scannerAgents.length ? "green" : "orange"} />
      </section>
      <Panel title="Scanner-Agenten" description="Live-Status der Windows-PCs, die neue Scanner-PDFs automatisch übernehmen.">
        {loading ? <LoadingState /> : scannerAgents.length === 0 ? <div className="notice-banner system-notice"><strong>Noch kein Scanner-Agent verbunden.</strong>&nbsp; Nach der Installation erscheint das Lager hier automatisch.</div> : <div className="site-list warehouse-status-list">
          {scannerAgents.map((agent) => {
            const presentation = scannerStatus(agent);
            const warehouse = warehouses.find((item) => item.id === agent.warehouse_id)?.name ?? "Lager";
            return <article key={agent.warehouse_id}><span><ScanLine size={18} /></span><div><strong>{warehouse} · {agent.device_name}</strong><small>Version {agent.agent_version} · Signal {formatDateTime(agent.last_heartbeat_at)} · Ordner {agent.source_folder}</small>{agent.last_import_at && <small>Letzter Import: {formatDateTime(agent.last_import_at)}{agent.last_import_file_name ? ` · ${agent.last_import_file_name}` : ""}</small>}{agent.last_error && <small>{agent.last_error}</small>}</div><StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge></article>;
          })}
        </div>}
      </Panel>
      <Panel title="Dispo-Benachrichtigung" description="Neue Einlagerungen und abgeschlossene Abholungen werden in die Warteschlange gestellt.">
        <form className="inline-form" onSubmit={saveEmail}>
          <Field label="Dispo-E-Mail" hint="Leer lassen, um keine Empfängeradresse zu hinterlegen."><Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={exampleTexts.email} /></Field>
          <Button type="submit" disabled={busy}><Save size={16} /> Speichern</Button>
        </form>
        <div className="notice-banner system-notice"><strong>Noch kein Versanddienst verbunden.</strong>&nbsp; Die Vorgänge gehen nicht verloren, bleiben aber bis zur Einrichtung eines E-Mail-Anbieters in der Warteschlange.</div>
      </Panel>
      <Panel title="Letzte Dispo-Ereignisse" description="Technischer Nachweis der erzeugten Meldungen.">
        {loading ? <LoadingState /> : <div className="table-shell table-shell--embedded"><table><thead><tr><th>Zeit</th><th>Ereignis</th><th>Empfänger</th><th>Status</th><th>Versuche</th></tr></thead><tbody>{queue.map((row) => <tr key={row.id}><td>{formatDateTime(row.created_at)}</td><td>{row.event_type === "goods_receipt_stored" ? "Einlagerung" : "Abholung"}</td><td>{row.recipient || "Noch nicht gesetzt"}</td><td><StatusBadge tone={row.status === "completed" ? "success" : row.status === "failed" ? "danger" : "warning"}>{row.status === "completed" ? "Versendet" : row.status === "failed" ? "Fehler" : "Wartet"}</StatusBadge>{row.last_error && <span className="table-subline">{row.last_error}</span>}</td><td>{row.attempts}</td></tr>)}</tbody></table></div>}
      </Panel>
    </>
  );
}
